import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { useToast } from '../hooks/use-toast';
import { ArrowLeft, Loader2 } from 'lucide-react';
import AppleSignInButton from './AppleSignInButton';

const LOGO = "https://customer-assets.emergentagent.com/job_buraq-transport/artifacts/0a9wh5vw_0E081036-6309-4011-9127-0A9FE497D0F8.png";

const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({ email: '', password: '' });

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(formData.email, formData.password);
      toast({ title: 'Welcome back!', description: `Good to see you again, ${user.firstName}.` });
      navigate('/dashboard');
    } catch (error) {
      toast({ title: 'Login Failed', description: error.message || 'Invalid email or password.', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white relative overflow-hidden flex items-center justify-center px-5">
      <div className="pointer-events-none absolute -top-24 left-1/2 -translate-x-1/2 w-[520px] h-[520px] rounded-full bg-orange-600/15 blur-[120px]" />
      <div className="pointer-events-none absolute bottom-0 right-0 w-[360px] h-[360px] rounded-full bg-orange-500/10 blur-[120px]" />

      <button
        data-testid="back-btn"
        onClick={() => navigate('/')}
        className="absolute left-5 top-6 w-11 h-11 rounded-full border border-white/10 flex items-center justify-center text-gray-300 hover:text-white hover:border-orange-500/40 active:scale-95 transition-all"
        aria-label="Back"
      >
        <ArrowLeft className="h-4 w-4" />
      </button>

      <div className="relative w-full max-w-md">
        <div className="flex flex-col items-center text-center mb-8">
          <img src={LOGO} alt="RideBuraq" className="w-14 h-14 object-contain mb-5" />
          <p className="text-[11px] tracking-[0.3em] uppercase text-orange-400/80 mb-2">Welcome Back</p>
          <h1 className="text-4xl font-semibold tracking-tight">Sign In</h1>
        </div>

        <div className="rounded-3xl border border-white/10 bg-white/[0.02] p-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-gray-400 text-xs tracking-wide uppercase">Email</Label>
              <Input
                id="email" name="email" type="email" required
                value={formData.email} onChange={handleChange}
                className="mt-1.5 bg-white/[0.03] border-white/10 text-white placeholder-gray-600 focus:border-orange-500 focus:ring-orange-500 rounded-xl"
                placeholder="you@example.com"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-gray-400 text-xs tracking-wide uppercase">Password</Label>
              <Input
                id="password" name="password" type="password" required
                value={formData.password} onChange={handleChange}
                className="mt-1.5 bg-white/[0.03] border-white/10 text-white placeholder-gray-600 focus:border-orange-500 focus:ring-orange-500 rounded-xl"
                placeholder="Your password"
              />
              <div className="mt-2 text-right">
                <Link to="/forgot-password" data-testid="forgot-password-link" className="text-xs text-orange-400 hover:text-orange-300">Forgot password?</Link>
              </div>
            </div>
            <button
              type="submit"
              data-testid="login-submit-btn"
              disabled={loading}
              className="w-full rounded-full py-3.5 mt-2 text-base font-semibold text-white bg-gradient-to-r from-orange-500 to-orange-600 shadow-lg shadow-orange-900/30 hover:shadow-orange-500/30 active:scale-[0.99] transition-all disabled:opacity-40 disabled:pointer-events-none flex items-center justify-center"
            >
              {loading ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" />Signing In...</>) : 'Sign In'}
            </button>
          </form>

          <div className="flex items-center gap-3 my-5">
            <div className="h-px flex-1 bg-white/10" />
            <span className="text-[11px] tracking-wide uppercase text-gray-600">or</span>
            <div className="h-px flex-1 bg-white/10" />
          </div>
          <AppleSignInButton />
        </div>

        <p className="text-center text-gray-500 text-sm mt-6">
          Don't have an account?{' '}
          <Link to="/signup" className="text-orange-400 hover:text-orange-300 font-medium">Sign up</Link>
        </p>
      </div>
    </div>
  );
};

export default Login;
